import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Download, Bot, Check } from "lucide-react";
import * as api from "../lib/api";

export default function ReportDetailPage() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.getReport(id)
      .then(setReport)
      .catch(err => setError(err.response?.status === 404 ? "Report not found." : "Couldn't load report."));
  }, [id]);

  const [copied, setCopied] = useState(false);

  // Prompt for the whyrank-read-report skill, which reads this saved report over MCP
  // instead of re-running the analysis.
  const handleCopyPrompt = async () => {
    await navigator.clipboard.writeText(
      `Use the whyrank-read-report skill to read WHYRANK report ${report.id} ("${report.title}") and explain it with next steps.`
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const handleDownload = () => {
    const blob = new Blob([report.html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${report.title.replace(/[^\w-]+/g, "-").toLowerCase()}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Link to="/dashboard/reports" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3">
        <ChevronLeft size={14} /> Reports
      </Link>
      {error && <p className="text-sm text-red-500">{error}</p>}
      {!report && !error && <p className="text-sm text-zinc-400">Loading…</p>}
      {report && (
        <>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-zinc-900 truncate">{report.title}</h1>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                {new Date(report.created_at).toLocaleString()}
                {report.period_start && report.period_end && <> · covers {report.period_start} → {report.period_end}</>}
                {report.source && <> · {report.source}</>}
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopyPrompt}
                title="Copy a prompt that asks your agent to read and explain this report"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-zinc-100 bg-white text-zinc-900 hover:bg-zinc-50 rounded-lg"
              >
                {copied ? <Check size={14} strokeWidth={2} /> : <Bot size={14} strokeWidth={2} />}
                {copied ? "Copied" : "Ask your agent"}
              </button>
              <button
                type="button"
                onClick={handleDownload}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-zinc-100 bg-white text-zinc-900 hover:bg-zinc-50 rounded-lg"
              >
                <Download size={14} strokeWidth={2} /> Download HTML
              </button>
            </div>
          </div>
          {/* Agent-written HTML: scripts allowed for charts, but no allow-same-origin,
              so it runs on an opaque origin with no access to the dashboard or its tokens. */}
          <iframe
            title={report.title}
            srcDoc={report.html}
            sandbox="allow-scripts allow-popups"
            className="w-full bg-white rounded-xl border border-zinc-100 shadow-sm"
            style={{ height: "calc(100vh - 180px)", minHeight: 480 }}
          />
        </>
      )}
    </div>
  );
}
